import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";

import Alert from "veneer/Alert";
import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Button from "veneer/Button";
import VeneerGrid from "veneer/Grid";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import Flags from "cortex/ui/src/fleet/constant/flags";
import osMetaMap from "cortex/ui/src/fleet/constant/osPlatform";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { Network } from "cortex/ui/src/network/model";
import { getRuntimeConfig } from "cortex/ui/src/runtime/config";
import BackButton from "cortex/ui/src/ui/button";
import { Features } from "cortex/ui/src/ui/features";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { Option } from "cortex/ui/src/ui/table/types";
import ToggleSection from "cortex/ui/src/ui/toggle-section";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";
import { Container, Panel, StickyPageActions } from "cortex/ui/src/view/layout";

import type { UserFlag } from "./model";

declare function require(moduleName: string): unknown;
type SemverCompare = (version: string, other: string) => boolean;
const semverGt = require("semver/functions/gt") as SemverCompare;
const semverLt = require("semver/functions/lt") as SemverCompare;

const DEVICE_TYPE = "device" as const;
const NETWORK_TYPE = "network" as const;
const EMPTY_USER_FLAGS: ReadonlyArray<UserFlag> = [];

interface FlagValidity {
  endsIn?: string;
  startsIn: string;
}

interface FlagDefinition {
  default?: string;
  endAdornment?: string;
  explanation: string;
  explanationLink?: {
    href: string;
    label: string;
  };
  flagType: string;
  key: string;
  label: string;
  options?: Option[];
  platforms: string[];
  type:
    | ArrayConstructor
    | BooleanConstructor
    | NumberConstructor
    | StringConstructor;
  validity?: FlagValidity;
}

interface FlagSupport {
  isNetwork?: boolean;
  isSupported: boolean;
  reason: string;
}

interface FlagsPageProps {
  model: Device | Network | null;
  networkUserFlags?: ReadonlyArray<UserFlag>;
  readonly: boolean;
  storesLoaded: boolean;
  type: typeof DEVICE_TYPE | typeof NETWORK_TYPE;
  updateFlags: (args: {
    flags: Record<string, string>;
    id: number | string;
  }) => Promise<unknown>;
}

const Grid = styled(VeneerGrid)({
  display: "flex",
});

export default function FlagsPage({
  model,
  networkUserFlags = EMPTY_USER_FLAGS,
  readonly,
  storesLoaded,
  type,
  updateFlags,
}: FlagsPageProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const [formFlags, setFormFlags] = useState<Record<string, unknown>>({});
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showDeviceOnlyFlags, setShowDeviceOnlyFlags] = useState(false);

  const isDevice = useCallback(() => type === DEVICE_TYPE, [type]);
  const isNetwork = useCallback(() => type === NETWORK_TYPE, [type]);
  const device = isDevice() && model ? (model as Device) : null;
  const network = isNetwork() && model ? (model as Network) : null;
  const deviceOs = device?.os_platform;

  const isFlagVisibleForDevice = useCallback(
    (flag: FlagDefinition) => {
      if (!isDevice() || !showDeviceOnlyFlags || !deviceOs) {
        return true;
      }

      const { platforms } = flag;

      if (!Array.isArray(platforms) || platforms.length === 0) {
        return true;
      }

      return platforms.includes(deviceOs);
    },
    [deviceOs, isDevice, showDeviceOnlyFlags],
  );

  const getVisibleFlags = useCallback(
    (flags: FlagDefinition[]) => {
      return flags.filter(isFlagVisibleForDevice);
    },
    [isFlagVisibleForDevice],
  );

  const getFlagsAsObject = useCallback(
    (userFlags?: ReadonlyArray<UserFlag>): Record<string, unknown> => {
      if (!userFlags) {
        return {};
      }

      const flags = userFlags.reduce<Record<string, unknown>>((acc, flag) => {
        acc[flag.name] = flag.value;
        return acc;
      }, {});

      Object.values(Flags).forEach((flag) => {
        const typedFlag = flag as FlagDefinition;
        const { key, type: flagType } = typedFlag;

        if (key in flags && flagType === Boolean) {
          flags[key] = flags[key] === "true";
        }
      });

      return flags;
    },
    [],
  );

  const setFlagsFromProps = useCallback(
    (
      nextModel: Device | Network | null,
      nextNetworkUserFlags: ReadonlyArray<UserFlag>,
    ) => {
      if (!nextModel) {
        return;
      }

      const { user_flags: userFlags } = nextModel;
      let flags = getFlagsAsObject(userFlags);

      if (isDevice()) {
        const overrideKey = (Flags.OVERRIDE_NETWORK_FLAGS as FlagDefinition)
          .key;
        const overrideFlag = flags[overrideKey];
        const setFlagCount = Object.keys(flags).length;

        if (
          (overrideFlag === false && setFlagCount > 1) ||
          (overrideFlag === undefined && setFlagCount > 0)
        ) {
          flags[overrideKey] = true;
        } else if (!flags[overrideKey]) {
          flags = getFlagsAsObject(nextNetworkUserFlags);
        }
      }

      setFormFlags(flags);
    },
    [getFlagsAsObject, isDevice],
  );

  useEffect(() => {
    setFlagsFromProps(model, networkUserFlags);
  }, [model, networkUserFlags, setFlagsFromProps]);

  const buildFlags = useCallback(
    (sourceFlags: Record<string, unknown>): Record<string, string> => {
      const finalFlags: Record<string, string> = {};

      Object.values(Flags).forEach((flag) => {
        const typedFlag = flag as FlagDefinition;
        const value = sourceFlags[typedFlag.key];

        if (value === null || value === undefined) {
          return;
        }

        finalFlags[typedFlag.key] = String(value);
      });

      return finalFlags;
    },
    [],
  );

  const updateArrayFlag = useCallback(
    (valueObj: Option | null, key: string) => {
      setFormFlags((prev) => ({
        ...prev,
        [key]: valueObj ? valueObj.value : null,
      }));
    },
    [],
  );

  const updateBooleanFlag = useCallback((checked: boolean, key: string) => {
    setFormFlags((prev) => ({
      ...prev,
      [key]: checked,
    }));
  }, []);

  const updateTextFlag = useCallback((value: string, key: string) => {
    setFormFlags((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const groupFlags = useCallback(() => {
    const flagValues = Object.values(Flags) as FlagDefinition[];

    const watchdogFlags = flagValues.filter(
      (flag) => flag.flagType === "watchdog",
    );
    const windowFlags = flagValues.filter((flag) => flag.flagType === "window");
    const otherFlags = flagValues.filter((flag) => flag.flagType === "other");
    const deviceOnlyFlag = flagValues.find(
      (flag) => flag.flagType === "device",
    );
    const webcamFlags = flagValues.filter((flag) => flag.flagType === "webcam");

    return [
      watchdogFlags,
      windowFlags,
      otherFlags,
      deviceOnlyFlag,
      webcamFlags,
    ] as const;
  }, []);

  const parseFlagSupport = useCallback(
    (device?: Device, flagValidity?: FlagValidity): FlagSupport => {
      if (!flagValidity) {
        return {
          isSupported: true,
          reason: "",
        };
      }

      if (isNetwork()) {
        let reason =
          "This flag will be set to the default value on player versions lower than " +
          flagValidity.startsIn;

        if (flagValidity.endsIn) {
          reason += ` or higher than ${flagValidity.endsIn}`;
        }

        return {
          isNetwork: true,
          isSupported: true,
          reason,
        };
      }

      if (!device || !device.current_version) {
        const runtimeCfg = getRuntimeConfig();

        if ((runtimeCfg && runtimeCfg.NODE_ENV) === "development") {
          // eslint-disable-next-line no-console
          console.warn("No player version detected and not on Network view");
        }

        let reason =
          "This flag will be set to the default value on player versions lower than " +
          flagValidity.startsIn;

        if (flagValidity.endsIn) {
          reason += ` or higher than ${flagValidity.endsIn}`;
        }

        return {
          isSupported: true,
          reason,
        };
      }

      //skip version support boundary checking for KMP players - show all flags for now
      if (device.player_type && device.player_type.startsWith("kmp")) {
        return {
          isSupported: true,
          reason: "",
        };
      }

      const { endsIn, startsIn } = flagValidity;

      if (endsIn && semverGt(device.current_version, endsIn)) {
        return {
          isSupported: false,
          reason: `Feature unavailable. Unsupported in Player Version > ${endsIn}`,
        };
      }

      if (semverLt(device.current_version, startsIn)) {
        return {
          isSupported: false,
          reason: `Feature unavailable. Unsupported in Player Version < ${startsIn}`,
        };
      }

      return {
        isSupported: true,
        reason: "",
      };
    },
    [isNetwork],
  );

  const getSuccessMessage = useCallback(() => {
    if (!model) {
      return null;
    }

    if (network) {
      return <Link to={`/networks/${network.id}`}>View Network Devices</Link>;
    }

    if (device) {
      return <Link to={`/devices/${device.device_id}`}>View Device</Link>;
    }

    return null;
  }, [device?.device_id, model, network]);

  const handleSubmit = useCallback(async () => {
    if (!model) {
      return;
    }

    if (!updateFlags) {
      return;
    }

    setSubmitted(true);

    try {
      await updateFlags({
        flags: buildFlags(formFlags),
        id: model.id,
      });

      setErrorMessage("");
      setSuccessMessage("Flags updated successfully.");

      window.scrollTo({
        behavior: "smooth",
        top: 0,
      });
    } catch (error) {
      setErrorMessage(getErrorMessage(error as Error | ResponseLike | null));
      setSuccessMessage("");
    } finally {
      setSubmitted(false);
    }
  }, [buildFlags, formFlags, model, updateFlags]);

  const renderFlag = useCallback(
    (flag: FlagDefinition, disableFlag: boolean) => {
      const {
        endAdornment = "",
        explanation,
        explanationLink,
        key,
        label,
        platforms,
        type: flagType,
        validity,
      } = flag;

      const flagSupport = parseFlagSupport(device || undefined, validity);
      const isDisabled = readonly || disableFlag || !flagSupport.isSupported;
      const isNetworkFlag = flagSupport.isNetwork;
      const unsupported = !flagSupport.isSupported;

      if (
        key === (Flags.ENABLE_AUDIO as FlagDefinition).key &&
        !Features.isEnabled(Features.constants.ENABLE_AUDIO_FLAG)
      ) {
        return null;
      }

      const defaultExplanation = flagType !== Boolean && flag.default && (
        <>
          (default: <b>{flag.default}</b>)
        </>
      );

      const helpBlock = (
        <Typography component="span" variant="body2">
          <Box component="span" display="flex" flexDirection="column" gap={0.5}>
            {(unsupported || isNetworkFlag) && (
              <Alert dense severity="info">
                {flagSupport.reason}
              </Alert>
            )}
            <Box component="span">
              {explanationLink ? (
                <>
                  {String(explanation).replace(explanationLink.label, "")}
                  <a href={explanationLink.href} target="_blank">
                    {explanationLink.label}
                  </a>
                  .
                </>
              ) : (
                explanation
              )}
              {defaultExplanation}
            </Box>
            {platforms.length > 0 && (
              <Box
                alignItems="center"
                component="span"
                display="flex"
                flexDirection="row"
                gap={0.5}
              >
                <>Supported Platforms: </>
                {platforms
                  .filter((platform) => osMetaMap[platform])
                  .map((platform) => {
                    const { iconClass, label } = osMetaMap[platform];
                    return (
                      <i
                        aria-hidden="true"
                        className={`fa ${iconClass}`}
                        key={platform}
                        title={label}
                      />
                    );
                  })}
              </Box>
            )}
          </Box>
        </Typography>
      );

      if (flagType === Boolean) {
        return (
          <ToggleSection
            checked={!!formFlags[key]}
            checkedLabel="On"
            description={helpBlock}
            disabled={isDisabled}
            key={key}
            onChange={() =>
              updateBooleanFlag(!(formFlags[key] as boolean), key)
            }
            title={label}
            uncheckedLabel="Off"
          ></ToggleSection>
        );
      }

      if (flagType === Array) {
        const options = flag.options || [];
        const selectedValue = formFlags[key] as null | string | undefined;
        // null, never undefined: the flags land in state after the first
        // render, and undefined would leave the input stuck uncontrolled
        const selectedOption: Option | null =
          options.find((option) => option.value === selectedValue) ?? null;

        return (
          <InputSection
            description={helpBlock}
            inputField={
              <AutocompleteSelectInput<Option, false, boolean, false>
                disableClearable
                disabled={isDisabled}
                inputProps={{
                  label: label,
                  sx: {
                    "& .MuiAutocomplete-inputRoot": {
                      backgroundColor: Colors.white,
                    },
                  },
                }}
                isOptionEqualToValue={(option: Option, value: Option) =>
                  option.value === value.value
                }
                onChange={(_e: any, value: Option | null) =>
                  updateArrayFlag(value, key)
                }
                options={options}
                size="small"
                value={selectedOption}
              />
            }
            key={key}
          />
        );
      }

      return (
        <InputSection
          description={helpBlock}
          inputField={
            <TextInput
              bgcolor={Colors.white}
              disabled={isDisabled}
              endAdornment={endAdornment}
              fullWidth
              label={label}
              onChange={(value: string | null) =>
                updateTextFlag(value ?? "", key)
              }
              value={(formFlags[key] as string | undefined) ?? ""}
            />
          }
          key={key}
        />
      );
    },
    [
      formFlags,
      device?.current_version,
      parseFlagSupport,
      readonly,
      updateArrayFlag,
      updateBooleanFlag,
      updateTextFlag,
    ],
  );

  const renderBreadcrumbs = useCallback(
    (name?: string) => {
      if (!name || !model) {
        return null;
      }

      const path = device
        ? `/devices/${device.device_id}`
        : `/networks/${network?.id}`;

      return getBreadcrumbs([
        {
          label: isNetwork() ? "Networks" : "Devices",
          target: isNetwork() ? "/networks" : "/devices",
        },
        { label: name, target: path },
      ]);
    },
    [device, isDevice, isNetwork, network, model],
  );

  const renderFlags = useCallback(
    (flags: FlagDefinition[]) => {
      const overrideKey = (Flags.OVERRIDE_NETWORK_FLAGS as FlagDefinition).key;
      const isDisabled = isDevice() && !formFlags[overrideKey];

      return flags.map((flag) => renderFlag(flag, !!isDisabled));
    },
    [formFlags, isDevice, renderFlag],
  );

  const renderBottomActions = useCallback(() => {
    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <BackButton />
        {!readonly && (
          <Button disabled={submitted} onClick={handleSubmit}>
            Save
          </Button>
        )}
        <Typography component="span">
          <strong>Important:</strong> Device must be restarted for any changes
          to be implemented.
        </Typography>
      </Stack>
    );
  }, [handleSubmit, readonly, submitted]);

  const networkFlagHelpText = useMemo(() => {
    if (!isNetwork()) {
      return null;
    }

    return (
      <Panel p={1}>
        <Typography>
          {"Network flags are inherited by devices. However, devices " +
            'with "Override network flags with device flags" checked ' +
            "will use device flag settings instead of network flags."}
        </Typography>
      </Panel>
    );
  }, [isNetwork]);

  if (!storesLoaded) {
    return (
      <Container p={1}>
        <NavLoader.Proxy />
      </Container>
    );
  }

  if (!model) {
    return <Redirect to="/404" />;
  }

  const modelName = model.name || type;
  const [watchdogFlags, windowFlags, otherFlags, deviceOnlyFlag, webcamFlags] =
    groupFlags();

  const visibleWatchdogFlags = getVisibleFlags(watchdogFlags);
  const visibleWindowFlags = getVisibleFlags(windowFlags);
  const visibleOtherFlags = getVisibleFlags(otherFlags);
  const visibleWebcamFlags = getVisibleFlags(webcamFlags);

  const visiblePanels = [
    {
      key: "watchdog",
      title: "Watchdog Flags",
      flags: visibleWatchdogFlags,
    },
    {
      key: "window",
      title: "Window Flags",
      flags: visibleWindowFlags,
    },
    {
      key: "other",
      title: "Other Flags",
      flags: visibleOtherFlags,
    },
    {
      key: "webcam",
      title: "Webcam Snapshot Flags",
      flags: visibleWebcamFlags,
    },
  ].filter((panel) => panel.flags.length > 0);

  return (
    <Container
      breadcrumbs={renderBreadcrumbs(modelName) ?? undefined}
      hideBox
      p={1}
      title={`Configure flags for ${modelName}`}
    >
      <Notification
        error={errorMessage}
        success={successMessage}
        successChildren={getSuccessMessage()}
      />
      {isDevice() && deviceOnlyFlag && (
        <Panel>
          <Stack direction="row" gap={2}>
            {renderFlag(deviceOnlyFlag, false)}
            <ToggleSection
              checked={showDeviceOnlyFlags}
              checkedLabel="On"
              dataTestId={{ toggle: "show-device-only-flags-toggle" }}
              description={
                "When enabled, only show flags that are valid for this device. When disabled show all flags."
              }
              onChange={() => setShowDeviceOnlyFlags((prev) => !prev)}
              title="Show flags valid for this device only"
              uncheckedLabel="Off"
            />
          </Stack>
        </Panel>
      )}
      {networkFlagHelpText}
      <Grid container gap={2}>
        {visiblePanels.map((panel) => (
          <Grid item key={panel.key} width="calc(50% - 8px)">
            <Box display="flex" flex={1}>
              <Panel gap={2} title={panel.title}>
                {renderFlags(panel.flags)}
              </Panel>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Panel p={1}>
        <StickyPageActions>{renderBottomActions()}</StickyPageActions>
      </Panel>
    </Container>
  );
}

const InputSection = ({
  description,
  inputField,
}: {
  description: ReactNode;
  inputField: ReactNode;
}) => (
  <Panel bgcolor={Colors.gray100} gap={0.5}>
    {inputField}
    {description}
  </Panel>
);

export { DEVICE_TYPE, FlagsPage, NETWORK_TYPE };
